/**
 * MM MEDIA ROW — mobile carousel nav + progress bar
 * ----------------------------------------------------------------------------------------------------------------
 * The card grid (sections/mm-media-row.liquid) is a plain native horizontal-scroll flex row on mobile — this
 * script only adds two glass prev/next buttons and a progress-bar fill on top of it, both driven by the grid's
 * real scrollLeft. It never replaces or intercepts the native scroll: swiping keeps working exactly as before,
 * clicking a button just calls scrollTo() on the same element, and both the buttons' visibility and the progress
 * fill update from a single scroll listener either way.
 *
 * Progress/visibility are driven by the CURRENT CARD INDEX (scrollLeft rounded to the nearest card step), not
 * the raw scroll fraction — two reasons: (1) the fill is meant to read as "card 2 of 3", not a literal scrollbar,
 * so it's (index + 1) / count (33% / 66% / 100% for 3 cards, never starting near-empty on card 1), and (2)
 * rounding to the nearest card absorbs the few px of sub-pixel/scroll-restoration drift browsers can leave
 * scrollLeft at on load, which a raw "scrollLeft <= 2px" check doesn't — that drift was showing the prev arrow
 * on the very first card.
 */
(() => {
  function init(root) {
    const grid = root.querySelector('[data-mm-mr-grid]');
    const prev = root.querySelector('[data-mm-mr-prev]');
    const next = root.querySelector('[data-mm-mr-next]');
    // The progress bar is a sibling of .../__carousel, not a child of it, so it's not inside `root`.
    const fill = root.parentElement && root.parentElement.querySelector('[data-mm-mr-progress-fill]');

    if (!grid) return;

    const cards = Array.from(grid.children);
    const count = cards.length;

    if (!count) return;

    function cardStep() {
      const gap = parseFloat(getComputedStyle(grid).columnGap || getComputedStyle(grid).gap) || 0;
      return cards[0].getBoundingClientRect().width + gap;
    }

    function currentIndex() {
      const step = cardStep();
      if (!step) return 0;

      const index = Math.round(grid.scrollLeft / step);
      return Math.min(count - 1, Math.max(0, index));
    }

    function update() {
      const index = currentIndex();

      if (fill) {
        fill.style.width = `${((index + 1) / count) * 100}%`;
      }

      if (prev) prev.hidden = index === 0;
      if (next) next.hidden = index === count - 1;
    }

    function goToIndex(index) {
      const clamped = Math.min(count - 1, Math.max(0, index));
      grid.scrollTo({ left: clamped * cardStep(), behavior: 'smooth' });
    }

    if (prev) prev.addEventListener('click', () => goToIndex(currentIndex() - 1));
    if (next) next.addEventListener('click', () => goToIndex(currentIndex() + 1));

    let ticking = false;

    grid.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;

        window.requestAnimationFrame(() => {
          update();
          ticking = false;
        });
      },
      { passive: true }
    );

    window.addEventListener('resize', update);

    update();
  }

  document.querySelectorAll('[data-mm-mr]').forEach(init);
})();
